// Verwaltung der Buchungslinks — läuft im Formularbereich der Pille (Kalender-Seite,
// Muster: ChecklistenVorlagenVerwaltung — Liste ↔ Editor, dunkle Pillen-Klassen).
// Öffentliche Gegenseite: pages/public/BuchungsSeite.tsx (/buchen/:slug), gespeist von
// der Edge Function `buchung`. Ein Buchungslink erzeugt aus Zeitfenster-Regeln freie
// Slots; Gäste buchen selbst, der bestehende Outlook-Sync verschickt die Einladung.
import { useEffect, useState } from 'react';
import {
  ArrowLeft, Buildings, CalendarBlank, CalendarCheck, Check, Copy, Door, EnvelopeSimple, FloppyDisk, Globe, LinkSimple,
  Minus, PencilSimple, Plus, Timer, Trash,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { buchungBetreff, buchungText } from '../../../lib/termine/anfrage';
import EmailVerfassen from '../emails/EmailVerfassen';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormFeld, FormZeile } from '../../../components/island/form/FormFeld';
import { FormSegment } from '../../../components/island/form/FormSegment';
import { FormAuswahl } from '../../../components/island/form/FormAuswahl';
import { FormSuche } from '../../../components/island/form/FormSuche';
import { FormSchalter } from '../../../components/island/form/FormSchalter';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { FormLoeschKnopf } from '../../../components/island/form/FormLoeschKnopf';
import { FormKleinKnopf, FormRundKnopf } from '../../../components/island/form/FormKleinKnopf';
import { FormEintragKarte } from '../../../components/island/form/FormEintragKarte';
import { FormLaden } from '../../../components/island/form/FormLaden';
import { FormText } from '../../../components/island/form/FormText';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { ABSCHNITT_FARBE, CHIP, ZEILEN_ICON } from '../../../components/island/form/form-stil';
import { MAIL_FORM_META } from '../../../components/island/form/mailMeta';

const TYP_OPTIONS: { value: 'besichtigung' | 'allgemein'; label: string }[] = [
  { value: 'besichtigung', label: 'Besichtigung' },
  { value: 'allgemein', label: 'Allgemein' },
];

// Wochentag-Konvention der Slot-Logik: 1=Montag … 7=Sonntag.
const WOCHENTAGE = [
  { value: 1, label: 'Montag', kurz: 'Mo' },
  { value: 2, label: 'Dienstag', kurz: 'Di' },
  { value: 3, label: 'Mittwoch', kurz: 'Mi' },
  { value: 4, label: 'Donnerstag', kurz: 'Do' },
  { value: 5, label: 'Freitag', kurz: 'Fr' },
  { value: 6, label: 'Samstag', kurz: 'Sa' },
  { value: 7, label: 'Sonntag', kurz: 'So' },
];
const WOCHENTAG_KURZ: Record<number, string> = Object.fromEntries(WOCHENTAGE.map((w) => [w.value, w.kurz]));

interface Regel { wochentag: number; von: string; bis: string }

interface Buchungslink {
  id: string;
  slug: string;
  typ: 'besichtigung' | 'allgemein';
  titel: string;
  beschreibung: string | null;
  objekt_id: string | null;
  einheit_id: string | null;
  slot_dauer_minuten: number;
  vorlauf_stunden: number;
  horizont_tage: number;
  regeln: Regel[];
  aktiv: boolean;
  created_at: string;
  objekt?: { id: string; bezeichnung: string } | null;
  einheit?: { id: string; bezeichnung: string } | null;
}

interface Entwurf {
  id: string | null;
  typ: 'besichtigung' | 'allgemein';
  titel: string;
  beschreibung: string;
  objektId: string;
  einheitId: string;
  slotDauerMinuten: number;
  vorlaufStunden: number;
  horizontTage: number;
  regeln: Regel[];
  aktiv: boolean;
}

const leererEntwurf = (): Entwurf => ({
  id: null,
  typ: 'besichtigung',
  titel: '',
  beschreibung: '',
  objektId: '',
  einheitId: '',
  slotDauerMinuten: 30,
  vorlaufStunden: 24,
  horizontTage: 21,
  regeln: [],
  aktiv: true,
});

// Slug clientseitig erzeugen — 8 Zeichen a-z0-9; unique-Constraint in der DB fängt
// die (extrem seltene) Kollision beim Insert ab.
const generiereSlug = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map((b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36])
    .join('');

// Regel-Kurzfassung für die Liste, z. B. "Di, Do · 14:00–17:00 · 30 Min".
// Zeitfenster mit identischem von/bis werden zu einer Wochentags-Gruppe zusammengefasst.
const regelnKurzfassung = (regeln: Regel[], slotDauerMinuten: number): string => {
  if (regeln.length === 0) return 'Keine Zeitfenster hinterlegt';
  const gruppen = new Map<string, number[]>();
  regeln.forEach((r) => {
    const key = `${r.von}-${r.bis}`;
    gruppen.set(key, [...(gruppen.get(key) || []), r.wochentag]);
  });
  const teile = Array.from(gruppen.entries()).map(([zeitraum, tage]) => {
    const [von, bis] = zeitraum.split('-');
    const tageLabel = [...tage].sort((a, b) => a - b).map((t) => WOCHENTAG_KURZ[t] || '?').join(', ');
    return `${tageLabel} · ${von}–${bis}`;
  });
  return `${teile.join(' / ')} · ${slotDauerMinuten} Min`;
};

export default function BuchungslinksVerwaltung() {
  const [links, setLinks] = useState<Buchungslink[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const fehler = useFormFehler();
  const [kopiertId, setKopiertId] = useState<string | null>(null);
  const { openForm, closeForm } = useIslandActions();

  // Objekt-Typeahead (Muster TerminFormular: objekte-Suche mit Debounce + Chip).
  const [objektSearch, setObjektSearch] = useState('');
  const [objektResults, setObjektResults] = useState<{ id: string; bezeichnung: string; strasse: string; hausnummer: string }[]>([]);
  const [selectedObjekt, setSelectedObjekt] = useState<{ id: string; bezeichnung: string } | null>(null);
  const [einheiten, setEinheiten] = useState<{ id: string; bezeichnung: string }[]>([]);

  const lade = async () => {
    const { data, error } = await supabase
      .from('buchungslinks')
      .select('id, slug, typ, titel, beschreibung, objekt_id, einheit_id, slot_dauer_minuten, vorlauf_stunden, horizont_tage, regeln, aktiv, created_at, objekt:objekte(id, bezeichnung), einheit:objekt_einheiten(id, bezeichnung)')
      .order('created_at', { ascending: false });
    if (!error) setLinks((data as unknown as Buchungslink[]) || []);
    setLaedt(false);
  };
  useEffect(() => { void lade(); }, []);

  // Objekt-Suche mit Debounce (Muster TerminFormular).
  useEffect(() => {
    if (objektSearch.length < 2) { setObjektResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer').ilike('bezeichnung', `%${objektSearch}%`).limit(5);
      setObjektResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [objektSearch]);

  // Einheiten des gewählten Objekts nachladen (nur bei Typ Besichtigung relevant).
  useEffect(() => {
    if (!entwurf || entwurf.typ !== 'besichtigung' || !entwurf.objektId) { setEinheiten([]); return; }
    (async () => {
      const { data } = await supabase.from('objekt_einheiten').select('id, bezeichnung').eq('objekt_id', entwurf.objektId).order('bezeichnung', { ascending: true });
      setEinheiten(data || []);
    })();
  }, [entwurf?.objektId, entwurf?.typ]);

  const oeffneEditor = (l?: Buchungslink) => {
    if (l) {
      setEntwurf({
        id: l.id,
        typ: l.typ,
        titel: l.titel,
        beschreibung: l.beschreibung || '',
        objektId: l.objekt_id || '',
        einheitId: l.einheit_id || '',
        slotDauerMinuten: l.slot_dauer_minuten,
        vorlaufStunden: l.vorlauf_stunden,
        horizontTage: l.horizont_tage,
        regeln: l.regeln || [],
        aktiv: l.aktiv,
      });
      setSelectedObjekt(l.objekt_id ? { id: l.objekt_id, bezeichnung: l.objekt?.bezeichnung || '' } : null);
    } else {
      setEntwurf(leererEntwurf());
      setSelectedObjekt(null);
    }
    setObjektSearch('');
    setObjektResults([]);
    fehler.leeren();
  };

  const handleSpeichern = async () => {
    if (!entwurf || speichert) return;
    const titel = entwurf.titel.trim();
    if (!titel) { fehler.zeige('Bitte einen Titel angeben.', ['titel']); return; }
    if (entwurf.regeln.length === 0) { fehler.zeige('Bitte mindestens ein Zeitfenster anlegen.'); return; }
    const ungueltig = entwurf.regeln.some((r) => !r.von || !r.bis || r.von >= r.bis);
    if (ungueltig) {
      fehler.zeige('Bei jedem Zeitfenster muss die Startzeit vor der Endzeit liegen.',
        entwurf.regeln.flatMap((r, i) => (!r.von || !r.bis || r.von >= r.bis ? [`von${i}`, `bis${i}`] : [])));
      return;
    }

    setSpeichert(true);
    fehler.leeren();
    const istBesichtigung = entwurf.typ === 'besichtigung';
    const zeile = {
      typ: entwurf.typ,
      titel,
      beschreibung: entwurf.beschreibung.trim() || null,
      objekt_id: istBesichtigung ? (entwurf.objektId || null) : null,
      einheit_id: istBesichtigung ? (entwurf.einheitId || null) : null,
      slot_dauer_minuten: entwurf.slotDauerMinuten,
      vorlauf_stunden: entwurf.vorlaufStunden,
      horizont_tage: entwurf.horizontTage,
      regeln: entwurf.regeln,
      aktiv: entwurf.aktiv,
    };
    const res = entwurf.id
      ? await supabase.from('buchungslinks').update({ ...zeile, updated_at: new Date().toISOString() }).eq('id', entwurf.id)
      : await supabase.from('buchungslinks').insert({ ...zeile, slug: generiereSlug() });
    setSpeichert(false);
    if (res.error) { fehler.zeige(res.error.message); return; }
    setEntwurf(null);
    await lade();
  };

  // Rückfrage (früher window.confirm) steckt im Zwei-Klick-Knopf, Text unverändert.
  const handleLoeschen = async () => {
    if (!entwurf?.id || speichert) return;
    setSpeichert(true);
    const { error } = await supabase.from('buchungslinks').delete().eq('id', entwurf.id);
    setSpeichert(false);
    if (error) { fehler.zeige(error.message); return; }
    setEntwurf(null);
    await lade();
  };

  const kopiereLink = async (l: Buchungslink) => {
    const url = `${window.location.origin}/buchen/${l.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setKopiertId(l.id);
      setTimeout(() => setKopiertId((id) => (id === l.id ? null : id)), 2000);
    } catch {
      fehler.zeige(`Link konnte nicht kopiert werden: ${url}`);
    }
  };

  // Link an mehrere schicken, ohne dass sie sich sehen: alle ins Bcc. Jeder wählt dann
  // selbst einen freien Slot (User-Wunsch 2026-08-12).
  const perMailVerschicken = async (l: Buchungslink) => {
    const url = `${window.location.origin}/buchen/${l.slug}`;
    const { data: konten } = await supabase
      .from('email_konten').select('id, email_adresse, bezeichnung, absender_name')
      .eq('aktiv', true).order('ist_standard', { ascending: false }).limit(1);
    const konto = konten?.[0];
    if (!konto) { fehler.zeige('Kein aktives E-Mail-Postfach gefunden.'); return; }
    openForm(
      <EmailVerfassen
        variant="pille"
        konto={{ id: konto.id, email_adresse: konto.email_adresse, bezeichnung: konto.bezeichnung || konto.absender_name || konto.email_adresse }}
        autoText={buchungText(l, url)}
        draft={{ an: [], bcc: [], betreff: buchungBetreff(l), text_inhalt: '' }}
        onClose={() => closeForm()}
        onSent={() => closeForm()}
      />,
      'Buchungslink senden (Bcc)',
      640, MAIL_FORM_META,
    );
  };

  const setzeRegel = (index: number, patch: Partial<Regel>) => {
    if (!entwurf) return;
    setEntwurf({ ...entwurf, regeln: entwurf.regeln.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  };

  const entferneRegel = (index: number) => {
    if (!entwurf) return;
    setEntwurf({ ...entwurf, regeln: entwurf.regeln.filter((_, i) => i !== index) });
  };

  const addZeitfenster = () => {
    if (!entwurf) return;
    setEntwurf({ ...entwurf, regeln: [...entwurf.regeln, { wochentag: 2, von: '14:00', bis: '17:00' }] });
  };

  if (laedt) return <FormLaden />;

  // ── Editor ────────────────────────────────────────────────────────────────
  if (entwurf) {
    const besichtigung = entwurf.typ === 'besichtigung';
    return (
      <FormRahmen fehler={fehler.text} knopfleiste={
        <FormKnopfleiste
          abbrechen={{ label: 'Zur Liste', icon: ArrowLeft, onClick: () => { setEntwurf(null); fehler.leeren(); } }}
          zusatz={entwurf.id ? (
            <FormLoeschKnopf frage={`Buchungslink „${entwurf.titel}" wirklich löschen?`} tip="Buchungslink löschen" gesperrt={speichert}
              onLoeschen={() => void handleLoeschen()} />
          ) : undefined}
          haupt={{ label: 'Speichern', icon: FloppyDisk, typ: 'button', onClick: () => void handleSpeichern(), laedt: speichert }} />
      }>
        <FormAbschnitt titel="Buchungslink" icon={LinkSimple} farbe="blau" pflicht={1} fehlt={fehler.istFehler('titel') ? 1 : 0} index={0}>
          <FormSegment label="Typ" wert={entwurf.typ} optionen={TYP_OPTIONS.map((o) => ({ wert: o.value, label: o.label }))}
            onChange={(typ) => setEntwurf({ ...entwurf, typ })} />
          <FormFeld label="Titel" value={entwurf.titel} placeholder="z. B. Besichtigung Musterstraße 5" pflicht fehler={fehler.istFehler('titel')}
            onChange={(e) => { setEntwurf({ ...entwurf, titel: e.target.value }); fehler.korrigiert('titel'); }} />
          <FormFeld label="Beschreibung" value={entwurf.beschreibung} zeilen={3} placeholder="Öffentlicher Intro-Text auf der Buchungsseite..."
            onChange={(e) => setEntwurf({ ...entwurf, beschreibung: e.target.value })} />
        </FormAbschnitt>

        {besichtigung && (
          <FormAbschnitt titel="Objekt" icon={Buildings} farbe="gold" index={1}>
            <FormSuche label="Objekt" platzhalter="Objekt suchen..." tip="Objekt – Objekt der Besichtigung"
              wert={selectedObjekt ? selectedObjekt.bezeichnung : objektSearch} onChange={(e) => setObjektSearch(e.target.value)}
              offen mindestLaenge={2} festNachAuswahl ausgewaehlt={!!selectedObjekt}
              onEntfernen={() => { setSelectedObjekt(null); setEntwurf({ ...entwurf, objektId: '', einheitId: '' }); }}
              onSchliessen={() => setObjektResults([])}
              treffer={selectedObjekt ? [] : objektResults.map((o) => ({
                id: o.id, label: o.bezeichnung, unter: `${o.strasse} ${o.hausnummer}`, icon: Buildings,
                iconFlaeche: ZEILEN_ICON.gold.flaeche, iconFarbe: ZEILEN_ICON.gold.farbe,
              }))}
              onWaehlen={(id) => {
                const o = objektResults.find((x) => x.id === id);
                if (!o) return;
                setSelectedObjekt({ id: o.id, bezeichnung: o.bezeichnung });
                setEntwurf({ ...entwurf, objektId: o.id, einheitId: '' });
                setObjektSearch('');
                setObjektResults([]);
              }} />
            {entwurf.objektId && (
              <FormAuswahl label="Einheit" wert={entwurf.einheitId} onChange={(einheitId) => setEntwurf({ ...entwurf, einheitId })}
                optionen={[
                  { wert: '', label: '— Keine Einheit —', icon: Minus, iconFlaeche: ZEILEN_ICON.leer.flaeche, iconFarbe: ZEILEN_ICON.leer.farbe },
                  ...einheiten.map((e) => ({ wert: e.id, label: e.bezeichnung, icon: Door, iconFlaeche: ZEILEN_ICON.gedaempft.flaeche, iconFarbe: ZEILEN_ICON.gedaempft.farbe })),
                ]} />
            )}
          </FormAbschnitt>
        )}

        <FormAbschnitt titel="Slots" icon={Timer} farbe="orange" index={2}>
          <FormZeile>
            <FormFeld label="Slot-Dauer (Min)" typ="number" min={5} step={5} value={entwurf.slotDauerMinuten}
              onChange={(e) => setEntwurf({ ...entwurf, slotDauerMinuten: Number(e.target.value) || 0 })} />
            <FormFeld label="Vorlauf (Std.)" typ="number" min={0} value={entwurf.vorlaufStunden}
              onChange={(e) => setEntwurf({ ...entwurf, vorlaufStunden: Number(e.target.value) || 0 })} />
            <FormFeld label="Horizont (Tage)" typ="number" min={1} value={entwurf.horizontTage}
              onChange={(e) => setEntwurf({ ...entwurf, horizontTage: Number(e.target.value) || 0 })} />
          </FormZeile>
        </FormAbschnitt>

        <FormAbschnitt titel="Zeitfenster" icon={CalendarCheck} farbe="gruen" index={3}>
          {entwurf.regeln.length === 0 && <FormText leer>Noch keine Zeitfenster — mindestens eines wird benötigt.</FormText>}
          {entwurf.regeln.map((r, i) => (
            <FormZeile key={i} spalten="minmax(0,1.3fr) minmax(0,1fr) minmax(0,1fr) auto">
              <FormAuswahl label="Wochentag" wert={String(r.wochentag)} onChange={(v) => setzeRegel(i, { wochentag: Number(v) })}
                optionen={WOCHENTAGE.map((w) => ({ wert: String(w.value), label: w.label, icon: CalendarBlank, iconFlaeche: ZEILEN_ICON.gedaempft.flaeche, iconFarbe: ZEILEN_ICON.gedaempft.farbe }))} />
              <FormFeld label="Von" typ="time" value={r.von} fehler={fehler.istFehler(`von${i}`)}
                onChange={(e) => { setzeRegel(i, { von: e.target.value }); fehler.korrigiert(`von${i}`); }} />
              <FormFeld label="bis" typ="time" value={r.bis} fehler={fehler.istFehler(`bis${i}`)}
                onChange={(e) => { setzeRegel(i, { bis: e.target.value }); fehler.korrigiert(`bis${i}`); }} />
              <FormRundKnopf label="Zeitfenster löschen" icon={Trash} art="gefahr" onClick={() => entferneRegel(i)} />
            </FormZeile>
          ))}
          <FormKleinKnopf label="Zeitfenster" icon={Plus} onClick={addZeitfenster} tip="Zeitfenster – Weiteres Zeitfenster hinzufügen" />
        </FormAbschnitt>

        <FormAbschnitt titel="Sichtbarkeit" icon={Globe} farbe="violett" index={4}>
          <FormSchalter text="Aktiv (Buchungsseite ist erreichbar)" an={entwurf.aktiv} icon={Globe} verlauf={ABSCHNITT_FARBE.violett.verlauf}
            onChange={(aktiv) => setEntwurf({ ...entwurf, aktiv })} />
        </FormAbschnitt>
      </FormRahmen>
    );
  }

  // ── Liste ─────────────────────────────────────────────────────────────────
  return (
    <FormRahmen fehler={fehler.text} knopfleiste={
      <FormKnopfleiste haupt={{ label: 'Neuer Buchungslink', icon: Plus, typ: 'button', onClick: () => oeffneEditor() }} />
    }>
      <FormText>Öffentliche Buchungsseiten — Gäste wählen selbst einen freien Slot, daraus entsteht sofort ein Termin inkl. Outlook-Einladung.</FormText>
      {links.length === 0 && <FormText leer>Noch keine Buchungslinks.</FormText>}
      {links.map((l) => {
        const besichtigung = l.typ === 'besichtigung';
        return (
          <FormEintragKarte key={l.id} icon={LinkSimple} farbe="blau" titel={l.titel} gedimmt={!l.aktiv}
            chip={
              <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                color: besichtigung ? CHIP.gold.text : 'rgba(255,255,255,0.75)', background: besichtigung ? CHIP.gold.flaeche : 'rgba(255,255,255,0.1)' }}>
                {besichtigung ? 'Besichtigung' : 'Allgemein'}
              </span>
            }
            unter={`${regelnKurzfassung(l.regeln, l.slot_dauer_minuten)}${!l.aktiv ? ' · inaktiv' : ''}`}
            aktionen={[
              { label: 'Link kopieren', icon: kopiertId === l.id ? Check : Copy, farbe: kopiertId === l.id ? CHIP.gruen.text : undefined, onClick: () => void kopiereLink(l) },
              { label: 'Per Mail an mehrere senden (Empfänger im Bcc)', icon: EnvelopeSimple, onClick: () => void perMailVerschicken(l) },
              { label: 'Bearbeiten', icon: PencilSimple, onClick: () => oeffneEditor(l) },
            ]} />
        );
      })}
    </FormRahmen>
  );
}
